import { useState, useEffect } from 'react';
import { Plus, Layers } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SectionBuilder } from './SectionBuilder';
import type { ChecklistSecaoSchema } from '@portal-leo/shared';

interface ChecklistFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  comissoes: Array<{ id: string; nome: string }>;
  defaultComissaoId?: string;
  checklistId?: string;
}

export function ChecklistFormModal({
  isOpen,
  onClose,
  comissoes,
  defaultComissaoId,
  checklistId,
}: ChecklistFormModalProps) {
  const utils = trpc.useUtils();

  const [comissaoId, setComissaoId] = useState(defaultComissaoId || '');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [secoes, setSecoes] = useState<ChecklistSecaoSchema[]>([]);

  const { data: checklist, isLoading: isLoadingChecklist } = trpc.checklists.getById.useQuery(
    { id: checklistId! },
    { enabled: !!checklistId && isOpen }
  );

  const saveMutation = trpc.checklists.save.useMutation({
    onSuccess: () => {
      utils.checklists.list.invalidate();
      utils.checklists.countByEvento.invalidate();
      if (checklistId) {
        utils.checklists.getById.invalidate({ id: checklistId });
      }
      handleClose();
    },
  });

  // Reset comissaoId when modal opens with a new defaultComissaoId
  useEffect(() => {
    if (isOpen) {
      setComissaoId(defaultComissaoId || '');
    }
  }, [isOpen, defaultComissaoId]);

  useEffect(() => {
    if (checklist && checklistId) {
      setComissaoId(checklist.comissaoId);
      setNome(checklist.nome);
      setDescricao(checklist.descricao || '');
      setSecoes(
        checklist.secoes.map((s) => ({
          id: s.id,
          nome: s.nome,
          ordem: s.ordem,
          itens: s.itens.map((i) => ({
            id: i.id,
            nome: i.nome,
            descricao: i.descricao || undefined,
            tipo: i.tipo as any,
            config: i.config as any,
            obrigatorio: i.obrigatorio,
            ordem: i.ordem,
            dataPrazo: (i as any).dataPrazo || null,
          })),
        }))
      );
    }
  }, [checklist, checklistId]);

  const handleClose = () => {
    setComissaoId('');
    setNome('');
    setDescricao('');
    setSecoes([]);
    onClose();
  };

  const handleAddSection = () => {
    const newSection: ChecklistSecaoSchema = {
      nome: 'Nova secao',
      ordem: secoes.length,
      itens: [],
    };
    setSecoes([...secoes, newSection]);
  };

  const handleSectionChange = (index: number, section: ChecklistSecaoSchema) => {
    const newSecoes = [...secoes];
    newSecoes[index] = section;
    setSecoes(newSecoes);
  };

  const handleSectionRemove = (index: number) => {
    setSecoes(secoes.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate({
      id: checklistId,
      comissaoId,
      nome,
      descricao: descricao || undefined,
      secoes,
    });
  };

  const isEditing = !!checklistId;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Editar Checklist' : 'Novo Checklist'}
      size="lg"
    >
      {isLoadingChecklist && checklistId ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Comissão *</label>
            <select
              value={comissaoId}
              onChange={(e) => setComissaoId(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Selecione a comissão</option>
              {comissoes.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          </div>

          <Input
            label="Nome do Checklist"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex: Vistoria de Equipamentos"
            required
          />

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Descricao</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descricao opcional..."
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Secoes ({secoes.length})
                </span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={handleAddSection}>
                <Plus className="h-4 w-4 mr-1" />
                Adicionar
              </Button>
            </div>

            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {secoes.length === 0 ? (
                <p className="text-center text-gray-400 py-4 text-sm">
                  Nenhuma secao adicionada
                </p>
              ) : (
                secoes.map((section, index) => (
                  <SectionBuilder
                    key={section.id || index}
                    section={section}
                    onChange={(s) => handleSectionChange(index, s)}
                    onRemove={() => handleSectionRemove(index)}
                  />
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={saveMutation.isPending}>
              {isEditing ? 'Salvar' : 'Criar Checklist'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
